import { IconArrowRight } from '@/components/icons/IconArrowRight';
import { sanityImage } from '@/lib/sanity/image';
import type { BusinessAreaDto } from '@/lib/sanity/dto';
import { cn } from '@/lib/utils/tw-merge';
import { stegaClean } from '@/lib/utils/stega';
import { BaseLink } from './BaseLink';
import { businessAreaNavItem } from './navigation.utils';

type BusinessAreaRowProps = {
    area: BusinessAreaDto;
    /**
     * `section` is the large index row with the triangle marker and the sliding
     * action cluster; `compact` is the thumbnail row the mobile drawer uses.
     */
    variant?: 'section' | 'compact';
    /** Pointed at — lifts the name's weight, reveals the line and the action. */
    active?: boolean;
    /** Label beside the arrow, e.g. "Zum Bereich". Only shown in `section`. */
    actionLabel?: string;
    className?: string;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onFocus?: () => void;
};

/**
 * One Geschäftsbereich as a row. Presentation only — it owns no state, so the
 * mobile drawer and the Geschäftsbereiche section can both render it and keep
 * their own interaction.
 *
 * The thumbnail is decorative: the area's name sits right beside it, so an alt
 * text would only make the row read twice to a screen reader.
 */
export function BusinessAreaRow({
    area,
    variant = 'section',
    active = false,
    actionLabel,
    className,
    onClick,
    onMouseEnter,
    onFocus,
}: BusinessAreaRowProps) {
    const poster = sanityImage.heroPoster(area.previewPoster);

    return (
        <BaseLink
            item={businessAreaNavItem(area)}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            style={{ '--area': stegaClean(area.color) } as React.CSSProperties}
            className={cn(
                'business-area-row',
                variant === 'compact' && 'business-area-row--compact',
                active && 'is-active',
                className,
            )}
        >
            <span className="business-area-row__mark" aria-hidden="true" />

            {/* The drawer's rows are text-only, matching the mega menu — so the
                thumbnail is not merely hidden there, it is never requested. */}
            {variant === 'compact' ? null : (
                <span className="business-area-row__media" aria-hidden="true">
                    {poster ? (
                        // eslint-disable-next-line @next/next/no-img-element -- a fixed 84–104px thumbnail the CDN already sizes; next/image would only add an optimizer round trip
                        <img src={poster} alt="" loading="lazy" />
                    ) : null}
                </span>
            )}

            <span className="business-area-row__text">
                <span className="business-area-row__name-row">
                    <span className="business-area-row__name">{area.title}</span>
                    {actionLabel ? (
                        <span className="business-area-row__action" aria-hidden="true">
                            <span className="business-area-row__action-label">{actionLabel}</span>
                            <IconArrowRight />
                        </span>
                    ) : null}
                </span>

                <span className="business-area-row__meta">
                    <span className="business-area-row__line">{area.description}</span>
                </span>
            </span>

            <span className="business-area-row__go" aria-hidden="true">
                <IconArrowRight />
            </span>
        </BaseLink>
    );
}
